// Денежные суммы считаем в копейках (целые числа), чтобы не терять точность на float.
// Финансовое API отдаёт суммы строкой с двумя знаками ("1234.50"),
// а GET /autoparks по-прежнему отдаёт баланс числом.
export type Money = string | number;

const DECIMAL_RE = /^(-)?(\d+)(?:\.(\d{1,2}))?$/;
const INPUT_RE = /^(\d+)(?:\.(\d{0,2}))?$/;

// Сумма с бэкенда → копейки. NaN, если значение не похоже на деньги.
export function toKopecks(value: Money | null | undefined): number {
  if (value === null || value === undefined || value === '') return NaN;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.round(value * 100) : NaN;
  }

  const match = DECIMAL_RE.exec(value.trim());
  if (!match) return NaN;
  const [, minus, rubles, fraction = ''] = match;
  const kopecks = Number(rubles) * 100 + Number(fraction.padEnd(2, '0'));
  return minus ? -kopecks : kopecks;
}

// Ввод пользователя ("1 234,5") → копейки. null, если ввод некорректный.
export function parseMoneyInput(input: string): number | null {
  const normalized = input.replace(/\s/g, '').replace(',', '.');
  const match = INPUT_RE.exec(normalized);
  if (!match) return null;
  const [, rubles, fraction = ''] = match;
  return Number(rubles) * 100 + Number(fraction.padEnd(2, '0'));
}

// Копейки → десятичная строка для отправки на бэкенд ("1234.50").
export function kopecksToDecimal(kopecks: number): string {
  if (!Number.isSafeInteger(kopecks)) throw new Error('money: kopecks must be an integer');
  const sign = kopecks < 0 ? '-' : '';
  const abs = Math.abs(kopecks);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

// Сравнение суммы с балансом. 'unknown', если баланс не распознан: такую сумму нельзя пропускать.
export function checkAgainstBalance(kopecks: number, balance: Money | null | undefined): 'ok' | 'exceeds' | 'unknown' {
  const limit = toKopecks(balance);
  if (Number.isNaN(limit)) return 'unknown';
  return kopecks > limit ? 'exceeds' : 'ok';
}

// Сумма для показа: "1 234" или "1 234,50". Некорректное значение → "0".
export function formatMoney(value: Money | null | undefined): string {
  const kopecks = toKopecks(value);
  if (Number.isNaN(kopecks)) return '0';
  const digits = kopecks % 100 === 0 ? 0 : 2;
  return (kopecks / 100).toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
